import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Button, ButtonGroup, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import {
  ArrowBack,
  IosShareOutlined,
  DeleteOutline,
  EmailOutlined,
  MoreVert,
  PersonAddAlt1Outlined,
  Settings as SettingsIcon,
  VisibilityOutlined,
} from "@mui/icons-material";
import ClientDetailsShareModal from "components/Modals/ClientDetailsShareModal";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import DeleteModal from "components/Modals/DeleteModal";
import DisplayDate from "components/DisplayDate";
import { RefreshData } from "components/helper";
import { deleteData } from "const/axios";
import useKothar from "context/useKothar";
import { convertPrEnquiry, fetchPrEnquiryPage, prEnquiryUrl } from "services/prEnquiryApi";
import { asList, daysUntil, describeDays, readEnquiry, visaUrgency } from "./prEnquiryRecord";

const CONVERTED_FILTERS = [
  { label: "New", value: "not-converted" },
  { label: "Converted", value: "converted" },
  { label: "All", value: "all" },
];

const URGENCY_STYLES = {
  expired: "bg-rose-100 text-rose-700",
  urgent: "bg-rose-50 text-rose-700",
  soon: "bg-amber-50 text-amber-700",
  ok: "bg-emerald-50 text-emerald-700",
};

const Dash = () => <span className="text-app-text-muted">—</span>;

const VisaCell = ({ enquiry }) => {
  if (!enquiry.visaStatus && !enquiry.visaExpiry) return <Dash />;
  const days = daysUntil(enquiry.visaExpiry);
  const urgency = visaUrgency(days);
  return (
    <div className="min-w-0">
      <div className="truncate text-sm text-app-text">{enquiry.visaStatus || "—"}</div>
      {enquiry.visaExpiry ? (
        <span
          className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${
            URGENCY_STYLES[urgency] || ""
          }`}
        >
          {describeDays(days)}
        </span>
      ) : null}
    </div>
  );
};

const Chips = ({ values, max = 2 }) => {
  const list = asList(values);
  if (!list.length) return <Dash />;
  return (
    <div className="flex flex-wrap gap-1">
      {list.slice(0, max).map((value) => (
        <span
          key={value}
          className="rounded-full border border-primary px-2 py-0.5 text-[11px] font-medium text-primary whitespace-nowrap"
        >
          {value}
        </span>
      ))}
      {list.length > max ? (
        <span className="text-[11px] text-app-text-muted">+{list.length - max}</span>
      ) : null}
    </div>
  );
};

const RowActions = ({ enquiry, onView, onConvert, onDelete }) => {
  const [anchor, setAnchor] = useState(null);
  const close = () => setAnchor(null);
  const run = (action) => () => {
    close();
    action();
  };
  return (
    <>
      <IconButton
        size="small"
        aria-label={`Actions for ${enquiry.name || "this enquiry"}`}
        onClick={(event) => {
          event.stopPropagation();
          setAnchor(event.currentTarget);
        }}
      >
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        onClick={(event) => event.stopPropagation()}
      >
        <MenuItem onClick={run(onView)}>
          <VisibilityOutlined fontSize="small" className="mr-2" /> View profile
        </MenuItem>
        {enquiry.email ? (
          <MenuItem
            onClick={run(() =>
              window.open(
                `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
                  enquiry.email
                )}`,
                "_blank",
                "noopener,noreferrer"
              )
            )}
          >
            <EmailOutlined fontSize="small" className="mr-2" /> Send email
          </MenuItem>
        ) : null}
        {!enquiry.converted ? (
          <MenuItem onClick={run(onConvert)}>
            <PersonAddAlt1Outlined fontSize="small" className="mr-2" /> Convert to client
          </MenuItem>
        ) : null}
        <MenuItem onClick={run(onDelete)} sx={{ color: "error.main" }}>
          <DeleteOutline fontSize="small" className="mr-2" /> Delete
        </MenuItem>
      </Menu>
    </>
  );
};

/** Every PR pathway enquiry, newest first, with what matters for planning visible at a glance. */
const PrEnquiriesTable = () => {
  const navigate = useNavigate();
  const [{ token }] = useKothar();
  const [filters, setFilters] = useState({
    convertedFilter: "not-converted",
    search: "",
    pageIndex: 0,
    pageSize: 20,
  });
  const [pendingConvert, setPendingConvert] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    setFilters((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
  }, [filters.convertedFilter]);

  const pageQuery = useQuery(
    ["pr-enquiry-page", filters],
    () =>
      fetchPrEnquiryPage({
        page: filters.pageIndex,
        pageSize: filters.pageSize,
        search: filters.search,
        convertedFilter: filters.convertedFilter,
      }),
    { keepPreviousData: true, refetchOnWindowFocus: false, refetchOnMount: "always" }
  );

  const rows = useMemo(
    () => (pageQuery.data?.content ?? []).map((record) => readEnquiry(record)),
    [pageQuery.data]
  );
  const totalItems = pageQuery.data?.totalItems ?? 0;

  const { mutate: convert, isLoading: converting } = useMutation(convertPrEnquiry, {
    onSuccess: (converted) => {
      setPendingConvert(null);
      pageQuery.refetch();
      toast.success(`${converted?.name || "Enquiry"} converted to a client`);
    },
    onError: (error) => {
      setPendingConvert(null);
      toast.error(
        error?.response?.data?.errorMessage ||
          error?.response?.data?.message ||
          "Could not convert this enquiry"
      );
    },
  });

  const remove = () =>
    deleteData(
      prEnquiryUrl(pendingDelete?.id),
      () => {
        setPendingDelete(null);
        pageQuery.refetch();
      },
      token
    );

  const openProfile = (enquiry) => navigate(`/admin/client/pr-enquiries/${enquiry.id}`);

  const columns = useMemo(
    () => [
      {
        accessorKey: "createdAt",
        header: "Received",
        size: 110,
        Cell: ({ row }) => <DisplayDate value={row.original.createdAt} />,
      },
      {
        accessorKey: "name",
        header: "Name",
        size: 200,
        Cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate font-medium text-app-text">{row.original.name || "—"}</div>
            <div className="truncate text-xs text-app-text-secondary">
              {row.original.email || row.original.number || ""}
            </div>
          </div>
        ),
      },
      { accessorKey: "number", header: "Phone", size: 120 },
      {
        accessorKey: "visaStatus",
        header: "Visa",
        size: 190,
        Cell: ({ row }) => <VisaCell enquiry={row.original} />,
      },
      {
        accessorKey: "currentCourse",
        header: "Course",
        size: 200,
        Cell: ({ row }) => {
          const { currentCourse: course, currentCourseProvider: provider } = row.original;
          if (!course && !provider) return <Dash />;
          return (
            <div className="min-w-0">
              <div className="truncate text-sm text-app-text">{course || "—"}</div>
              <div className="truncate text-xs text-app-text-secondary">{provider || ""}</div>
            </div>
          );
        },
      },
      {
        accessorKey: "onshoreExperienceTotal",
        header: "Onshore exp.",
        size: 110,
        Cell: ({ row }) => row.original.onshoreExperienceTotal || <Dash />,
      },
      {
        accessorKey: "offshoreExperienceTotal",
        header: "Offshore exp.",
        size: 110,
        Cell: ({ row }) => row.original.offshoreExperienceTotal || <Dash />,
      },
      {
        accessorKey: "englishScore",
        header: "English",
        size: 110,
        Cell: ({ row }) => {
          const { englishTest: test, englishScore: score } = row.original;
          if (!test) return <Dash />;
          return (
            <span className="whitespace-nowrap">{[test, score].filter(Boolean).join(" ")}</span>
          );
        },
      },
      {
        accessorKey: "pathways",
        header: "Pathways",
        size: 230,
        Cell: ({ row }) => <Chips values={row.original.pathways} />,
      },
      {
        accessorKey: "timeline",
        header: "Timeline",
        size: 130,
        Cell: ({ row }) => row.original.timeline || <Dash />,
      },
      {
        accessorKey: "converted",
        header: "Status",
        size: 150,
        Cell: ({ row }) =>
          row.original.converted ? (
            <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
              Client
            </span>
          ) : (
            <Button
              size="small"
              variant="outlined"
              color="success"
              disabled={converting}
              onClick={(event) => {
                event.stopPropagation();
                setPendingConvert(row.original);
              }}
              sx={{ textTransform: "none", py: 0.15, whiteSpace: "nowrap" }}
            >
              Convert
            </Button>
          ),
      },
      {
        accessorKey: "actions",
        header: "",
        size: 60,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <RowActions
            enquiry={row.original}
            onView={() => openProfile(row.original)}
            onConvert={() => setPendingConvert(row.original)}
            onDelete={() => setPendingDelete(row.original)}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [converting]
  );

  const table = useMaterialReactTable({
    columns,
    data: rows,
    getRowId: (row) => String(row.id),
    enableRowNumbers: true,
    enableSorting: false,
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
    enableColumnPinning: true,
    enableStickyHeader: true,
    paginationDisplayMode: "pages",
    rowCount: totalItems,
    pageCount: Math.ceil(totalItems / filters.pageSize) || 0,
    onPaginationChange: (updater) =>
      setFilters((prev) => {
        const next =
          typeof updater === "function"
            ? updater({ pageIndex: prev.pageIndex, pageSize: prev.pageSize })
            : updater;
        return { ...prev, ...next };
      }),
    onGlobalFilterChange: (value) =>
      setFilters((prev) => ({ ...prev, search: value || "", pageIndex: 0 })),
    initialState: {
      density: "compact",
      showGlobalFilter: true,
      columnPinning: { left: ["mrt-row-numbers", "name"] },
    },
    muiSearchTextFieldProps: { placeholder: "Search name, email or phone" },
    muiTableContainerProps: { sx: { maxHeight: "70vh", minHeight: "50vh" } },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => openProfile(row.original),
      sx: { cursor: "pointer" },
    }),
    renderEmptyRowsFallback: () => (
      <div className="px-6 py-14 text-center">
        <p className="m-0 text-sm font-medium text-app-text">No PR enquiries here yet</p>
        <p className="m-0 mt-1 text-xs text-app-text-secondary">
          Share the form from Settings → PR Enquiry Form and new enquiries will appear here.
        </p>
      </div>
    ),
    state: {
      pagination: { pageIndex: filters.pageIndex, pageSize: filters.pageSize },
      globalFilter: filters.search,
      isLoading: pageQuery.isLoading,
      showProgressBars: pageQuery.isFetching,
    },
  });

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full px-4">
        <div className="relative flex w-full min-w-0 flex-col break-words rounded shadow-lg">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <h3 className="m-0 flex items-center gap-2 text-lg font-semibold text-app-text">
              <ArrowBack
                onClick={() => navigate("/admin/client")}
                className="cursor-pointer"
                aria-label="Back to clients"
              />
              PR Enquiries ({totalItems}) <RefreshData refreshData={pageQuery.refetch} />
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <ButtonGroup size="small" variant="outlined">
                {CONVERTED_FILTERS.map((option) => (
                  <Button
                    key={option.value}
                    variant={filters.convertedFilter === option.value ? "contained" : "outlined"}
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, convertedFilter: option.value }))
                    }
                  >
                    {option.label}
                  </Button>
                ))}
              </ButtonGroup>
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={<IosShareOutlined />}
                onClick={() => setSharing(true)}
              >
                Share form
              </Button>
              <Tooltip title="PR enquiry form settings">
                <IconButton
                  size="small"
                  onClick={() => navigate("/admin/settings#pr-enquiry-form")}
                  aria-label="PR enquiry form settings"
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
          <MaterialReactTable table={table} />
        </div>
      </div>

      {pendingDelete ? (
        <DeleteModal
          open
          item="PR enquiry"
          handleCancel={() => setPendingDelete(null)}
          handleDelete={remove}
        />
      ) : null}
      <ClientDetailsShareModal open={sharing} initialForm="pr" onClose={() => setSharing(false)} />
      <ConfirmDialog
        open={pendingConvert != null}
        title="Convert to client?"
        message={`${
          pendingConvert?.name || "This enquiry"
        } will be added to your clients using these details${
          pendingConvert?.email ? ` (${pendingConvert.email})` : ""
        }. If they are already a client, this links to the existing record instead.`}
        confirmLabel="Convert"
        confirmColor="success"
        loading={converting}
        onConfirm={() => convert(pendingConvert?.id)}
        onCancel={() => setPendingConvert(null)}
      />
    </div>
  );
};

export default PrEnquiriesTable;
